import React, {Component} from 'react';
import PropTypes from 'prop-types';
import IntersectionObserverManager from '../lazy-loader/IntersectionObserverManager';

/**
 * An element invisible to the user but not to the browser
 *
 * As per the spec, the observed target can have a zero area. Chrome doesn't always
 * work as expected though [2] so we create a translucid element instead.
 *
 * [1] https://w3c.github.io/IntersectionObserver/#dom-intersectionobserverentry-isintersecting
 * [2] https://crbug.com/992765
 */
export const INVISIBLE_ELEMENT = (
    <div
        style={{
            minWidth: '1px',
            minHeight: '1px',
            width: '100%',
            height: '100%',
        }}
    />
);

const observe = (element, callback, options) => {
    const observer = IntersectionObserverManager.getInstance(
        callback,
        options,
        element
    );
    observer.observe(element);
    return () => observer.unobserve(element);
};

/**
 * The IntersectionObserver API wrapped as a React component
 */
export default class ReactIntersectionObserver extends Component {
    static propTypes = {
        /** The element to observe. If a non-DOM element is provided, make sure it forwards refs (see https://reactjs.org/docs/forwarding-refs.html) */
        children: PropTypes.node,
        /** See https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin */
        rootMargin: PropTypes.string,
        /** Called on intersection changes with the relevant IntersectionObserverEntry */
        onIntersection: PropTypes.func.isRequired,
        /** percentage of the target's visibility the observer should be executed */
        threshold: PropTypes.number,
    };

    static defaultProps = {
        threshold: 0,
    };

    constructor(props) {
        super(props);
        this.target = React.createRef();
    }

    componentDidMount() {
        const {onIntersection, rootMargin, threshold} = this.props;

        this.unobserve = observe(this.target.current, onIntersection, {
            rootMargin,
            threshold,
        });
    }

    componentWillUnmount() {
        this.unobserve(this.target.current);
    }

    render() {
        const {children} = this.props;
        const child = children || INVISIBLE_ELEMENT;
        return React.cloneElement(child, {ref: this.target});
    }
}
